// GA4 conversion tracking for marketing lead forms.
//
// Every contact / audit / compliance form fires two events:
//   1. `generate_lead`         — on a successful submission (mark as a conversion in GA4)
//   2. `form_thank_you_view`   — when the success / thank-you state is rendered
//
// Each event is sent once: client-side via gtag.js when it loaded, otherwise
// (ad-blocked / gtag missing) via the Measurement Protocol relay. Never both,
// so GA4 never double-counts a submission.
//
// `generate_lead` is also sent to Customer.io Data Pipelines with the same
// name and properties, so the two tracking plans stay one vocabulary. The
// payload carries form metadata only (name, category, interest, score), never
// the visitor's contact details.

import { relayIfGtagBlocked } from "@/lib/ga4Tracking";
import { isGaDebugMode, isProdHost } from "@/lib/analyticsHost";
import { cioTrack } from "@/lib/customerio";
import { canSendClientAnalytics } from "@/lib/europeVisitor";

const GA4_MEASUREMENT_ID = "G-M09E85VL9J";

export type LeadFormCategory =
  | "contact"
  | "support"
  | "demo"
  | "consultation"
  | "assessment"
  | "compliance"
  | "privacy"
  | "resource"
  | "careers";

export interface LeadFormParams {
  /** Stable form identifier, e.g. `contact_form`. */
  form_name: string;
  /** Grouping used for conversion reporting. */
  form_category: LeadFormCategory;
  /** Free-form extras (interest, framework, score, ...). */
  [key: string]: unknown;
}

/** Fire a client-side gtag event. No-ops when gtag is unavailable. */
function sendGtagEvent(eventName: string, params: Record<string, unknown>): void {
  if (!isProdHost()) return;
  if (!canSendClientAnalytics()) return;
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  try {
    window.gtag("event", eventName, {
      send_to: GA4_MEASUREMENT_ID,
      page_path: `${window.location.pathname}${window.location.search}`,
      page_location: window.location.href,
      ...(isGaDebugMode() ? { debug_mode: 1 } : {}),
      ...params,
    });
  } catch {
    /* analytics must never break a form */
  }
}

function clean(params: LeadFormParams): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  );
}

/**
 * Track a successful lead-form submission as a GA4 conversion (`generate_lead`).
 * Fire-and-forget — never throws.
 */
export function trackFormConversion(params: LeadFormParams): void {
  const payload = clean(params);
  sendGtagEvent("generate_lead", payload);
  relayIfGtagBlocked("generate_lead", { params: payload });
  cioTrack("generate_lead", payload);
}

/**
 * Track the thank-you / success state being shown to the visitor.
 * Fire-and-forget — never throws.
 */
export function trackThankYouView(params: LeadFormParams): void {
  const payload = clean(params);
  sendGtagEvent("form_thank_you_view", payload);
  relayIfGtagBlocked("form_thank_you_view", { params: payload });
}
